import { ReactNode } from 'react';
import { PopoverContent, PopoverTrigger, PopoverWrapper } from './components/popover-primitives';

type PopoverSide = 'top' | 'right' | 'bottom' | 'left';
type PopoverAlign = 'start' | 'center' | 'end';

type PopoverProps = {
  /** Whether the popover is open */
  open?: boolean;
  /** The element (like a button) that triggers the popover to appear when clicked */
  triggerElement: ReactNode;
  /** The content rendered inside the popover panel */
  content: ReactNode;
  /** Optional footer element rendered at the bottom of the popover content */
  footerElement?: ReactNode;
  /** Whether the popover should be initially open. Defaults to false */
  defaultOpen?: boolean;
  /** The preferred placement of the popover relative to the trigger element. Defaults to 'bottom' */
  side?: PopoverSide;
  /** The alignment of the popover relative to the trigger element. Defaults to 'center' */
  align?: PopoverAlign;
  /** Callback fired when the popover's open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Accessible label for the popover trigger and content */
  ariaLabel?: string;
  /** Optional CSS class name to apply to the popover content */
  className?: string;
};

/**
 * Popover displays floating content when a trigger element is clicked.
 * Built on Radix UI's Popover primitives for robust accessibility and positioning.
 *
 * @example
 * ```tsx
 * <Popover
 *   triggerElement={<Button>Open Menu</Button>}
 *   content={<MenuContent />}
 *   side="right"
 *   align="start"
 *   ariaLabel="Menu popover"
 *   className="max-w-52"
 * />
 * ```
 *
 * @param props.triggerElement - Element that triggers the popover (usually a button)
 * @param props.content - Content to display in the popover panel
 * @param props.footerElement - Optional footer element displayed at the bottom of the popover
 * @param props.defaultOpen - Whether popover is initially open (default: false)
 * @param props.side - Preferred placement of popover (default: 'bottom')
 * @param props.align - Alignment relative to trigger (default: 'center')
 * @param props.onOpenChange - Callback when open state changes
 * @param props.ariaLabel - Accessible label for the popover
 * @param props.className - Optional CSS class name to apply to the popover content
 */
export const Popover = ({
  open,
  triggerElement,
  content,
  footerElement,
  defaultOpen = false,
  side = 'bottom',
  align = 'center',
  onOpenChange,
  ariaLabel,
  className,
}: PopoverProps) => {
  return (
    <PopoverWrapper open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild aria-label={ariaLabel}>
        {triggerElement}
      </PopoverTrigger>

      <PopoverContent side={side} align={align} aria-label={ariaLabel} className={className}>
        {content}

        {footerElement && <div className="rounded-b-xl bg-seapoint-gray-medium px-4 py-3">{footerElement}</div>}
      </PopoverContent>
    </PopoverWrapper>
  );
};
